// Popover - new in v2, no v1 counterpart. Click-triggered floating panel for
// content richer than a tooltip: short forms, action lists, explanations.
//
// The panel is author-written markup referenced by the trigger, so it keeps its
// own cascade and event listeners; the module only positions it (@floating-ui/dom
// with the `fixed` strategy, which also escapes ancestor `overflow: hidden`),
// manages visibility via [data-state] and wires up the a11y attributes.
//
// HTML contract:
//   <button type="button" data-popover="#pop-share">Zdieľať</button>
//   <div class="popover" id="pop-share">
//     <div class="popover__content">...</div>
//   </div>
//
// Per-trigger overrides: data-popover-placement, data-popover-offset,
// data-popover-arrow="false", data-popover-trigger="click|manual".
// Any [data-popover-close] element inside the panel closes it.

import { attachFloating, clearFloating, type Placement } from './floating';

export interface PopoverConfig {
  /** Preferred placement; flips automatically when out of viewport. */
  placement?: Placement;
  /** Distance between trigger and panel, px. */
  offset?: number;
  /** Viewport padding kept free by shift(), px. */
  padding?: number;
  /** Render a caret pointing at the trigger (injected as .popover__arrow). */
  arrow?: boolean;
  /** 'click' toggles on trigger click; 'manual' only via instance.show(). */
  trigger?: 'click' | 'manual';
  /** Close when clicking outside the panel and the trigger. */
  hideOnClickOutside?: boolean;
  /** Close on Escape. */
  hideOnEscape?: boolean;
  /** Move focus into the panel on open and back to the trigger on close. */
  manageFocus?: boolean;
}

/** Programmatic handle for a single popover, kept in Popover#popovers. */
export interface PopoverInstance {
  trigger: HTMLElement;
  panel: HTMLElement;
  isOpen(): boolean;
  show(): void;
  hide(): void;
  toggle(): void;
  destroy(): void;
}

// Must match the transition duration in styles/modules/_popovers.scss.
const TRANSITION_MS = 200;

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const defaultConfig: PopoverConfig = {
  placement: 'bottom',
  offset: 10,
  padding: 8,
  arrow: true,
  trigger: 'click',
  hideOnClickOutside: true,
  hideOnEscape: true,
  manageFocus: true,
};

let autoId = 0;

class SinglePopover implements PopoverInstance {
  trigger: HTMLElement;

  panel: HTMLElement;

  private config: PopoverConfig;

  private arrowEl: HTMLElement | null = null;

  private detachFloating: (() => void) | null = null;

  private visible = false;

  private hideTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(trigger: HTMLElement, panel: HTMLElement, config: PopoverConfig) {
    this.trigger = trigger;
    this.panel = panel;
    this.config = config;

    if (!panel.id) {
      autoId += 1;
      panel.id = `ms-popover-${autoId}`;
    }

    panel.setAttribute('role', 'dialog');
    panel.setAttribute('data-state', 'hidden');
    if (!panel.hasAttribute('tabindex')) panel.setAttribute('tabindex', '-1');

    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', panel.id);
    trigger.setAttribute('aria-expanded', 'false');

    if (config.arrow !== false && panel.dataset.popoverArrow !== 'false') {
      this.arrowEl = panel.querySelector('.popover__arrow');
      if (!this.arrowEl) {
        this.arrowEl = document.createElement('div');
        this.arrowEl.className = 'popover__arrow';
        panel.append(this.arrowEl);
      }
    }

    if (config.trigger !== 'manual') {
      trigger.addEventListener('click', this.onTriggerClick);
    }
    panel.addEventListener('click', this.onPanelClick);
  }

  isOpen(): boolean {
    return this.visible;
  }

  show = (): void => {
    clearTimeout(this.hideTimer);
    if (this.visible) return;
    this.visible = true;

    const { panel, trigger } = this;

    panel.removeAttribute('hidden');
    panel.classList.remove('hide');

    this.detachFloating?.();
    this.detachFloating = attachFloating(trigger, panel, {
      placement:
        (trigger.dataset.popoverPlacement as Placement | undefined) ?? this.config.placement,
      offset: Number(trigger.dataset.popoverOffset ?? this.config.offset),
      padding: this.config.padding,
      arrowElement: this.arrowEl,
      strategy: 'fixed',
    });

    trigger.setAttribute('aria-expanded', 'true');

    // The panel is hidden with opacity/visibility, so it is always laid out and
    // the transition runs even when the state flips synchronously. The first
    // layout flush covers panels the author additionally hid with [hidden] or
    // .hide; the second one applies the new visibility before we move focus,
    // because focus() is a silent no-op on an element the browser still
    // considers invisible (_popovers.scss flips visibility without delay on the
    // way in for exactly this reason).
    // Deliberately not requestAnimationFrame: it never fires while the document
    // is hidden (background tab) and would leave the panel invisible with
    // aria-expanded="true".
    void panel.offsetHeight;
    panel.setAttribute('data-state', 'visible');
    void panel.offsetHeight;

    if (this.config.manageFocus) {
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panel).focus();
    }

    document.addEventListener('keydown', this.onKeydown);
    document.addEventListener('mousedown', this.onDocumentMousedown);
  };

  hide = (): void => {
    if (!this.visible) return;
    this.visible = false;

    const { panel, trigger } = this;

    panel.setAttribute('data-state', 'hidden');
    trigger.setAttribute('aria-expanded', 'false');

    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('mousedown', this.onDocumentMousedown);

    if (this.config.manageFocus && panel.contains(document.activeElement)) {
      trigger.focus();
    }

    clearTimeout(this.hideTimer);
    this.hideTimer = setTimeout(() => {
      // Hand positioning back to the stylesheet once the panel is invisible.
      this.detachFloating?.();
      this.detachFloating = null;
      clearFloating(panel);
    }, TRANSITION_MS);
  };

  toggle = (): void => {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  };

  destroy(): void {
    clearTimeout(this.hideTimer);
    this.detachFloating?.();
    this.detachFloating = null;

    this.trigger.removeEventListener('click', this.onTriggerClick);
    this.panel.removeEventListener('click', this.onPanelClick);
    document.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('mousedown', this.onDocumentMousedown);

    this.trigger.removeAttribute('aria-expanded');
    this.trigger.removeAttribute('aria-controls');
    this.trigger.removeAttribute('aria-haspopup');

    this.panel.setAttribute('data-state', 'hidden');
    clearFloating(this.panel);
    this.arrowEl?.remove();
    this.arrowEl = null;
    this.visible = false;
  }

  private onTriggerClick = (event: MouseEvent): void => {
    event.preventDefault();
    this.toggle();
  };

  private onPanelClick = (event: MouseEvent): void => {
    const target = event.target as Element | null;
    if (target?.closest('[data-popover-close]')) {
      event.preventDefault();
      this.hide();
    }
  };

  private onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && this.config.hideOnEscape !== false) this.hide();
  };

  private onDocumentMousedown = (event: MouseEvent): void => {
    if (this.config.hideOnClickOutside === false) return;

    const target = event.target as Node;
    if (this.panel.contains(target) || this.trigger.contains(target)) return;

    this.hide();
  };
}

export default class Popover {
  selector: string;

  config: PopoverConfig;

  /** Triggers → their popover instance. */
  popovers = new Map<Element, PopoverInstance>();

  constructor(selector = '[data-popover]', config: PopoverConfig = {}) {
    this.selector = selector;
    this.config = { ...defaultConfig, ...config };

    this._init();
  }

  /** Look up an instance by its trigger or by the panel's id/selector. */
  get(target: Element | string): PopoverInstance | undefined {
    if (typeof target !== 'string') return this.popovers.get(target);

    return [...this.popovers.values()].find(
      instance => `#${instance.panel.id}` === target || instance.panel.id === target,
    );
  }

  destroy(): void {
    this.popovers.forEach(popover => popover.destroy());
    this.popovers.clear();
  }

  update(): void {
    this.destroy();
    this._init();
  }

  private _init(): void {
    document.querySelectorAll<HTMLElement>(this.selector).forEach(trigger => {
      const selector = trigger.dataset.popover;
      const panel = selector ? document.querySelector<HTMLElement>(selector) : null;

      if (!panel) {
        console.error(`Popover: panel "${selector ?? ''}" not found for`, trigger);
        return;
      }

      const triggerConfig: PopoverConfig = {};
      if (trigger.dataset.popoverTrigger === 'manual') triggerConfig.trigger = 'manual';

      this.popovers.set(
        trigger,
        new SinglePopover(trigger, panel, { ...this.config, ...triggerConfig }),
      );
    });
  }
}
